import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, Info, Zap, Cpu, Link as LinkIcon, Check, GitCompare } from 'lucide-react';
import SVGViewer from '../components/SVGViewer';
import { richContentBodyStyles } from '../components/richContentBodyStyles';
import { boardsAPI } from '../services/api';
import { getBoardCategoryIcon, isMicrocontrollerCategory } from '../utils/boardCategoryIcons';
import {
  formatBoardVoltage,
  formatPinCurrent,
  formatPinVoltage
} from '../utils/compatibilityDisplay';
import {
  PROTOCOL_OPTIONS,
  normalizeSupportedProtocols,
  withBoardCompatibilityDefaults,
  withPinCompatibilityDefaults
} from '../utils/compatibilityOptions';
import { buildSmartCompare } from '../utils/smartCompare';
import {
  buildPinoutPreviewSvg,
  normalizeRotation,
  rotateCallouts
} from '../utils/pinCalloutLayout';
import {
  normalizeSvgViewBox,
  parseSvgViewBoxFromMarkup,
  rotateSvgViewBox
} from '../utils/svgViewBox';
import {
  buildComparePath,
  parseCompareBoardIdentifiersFromSearch,
  resolveCompareBoardSelection
} from '../utils/compareUrls';

const PROTOCOL_LABELS = Object.fromEntries(
  PROTOCOL_OPTIONS.map(({ value, label }) => [value, label])
);

const PROTOCOL_SORT_ORDER = Object.fromEntries(
  PROTOCOL_OPTIONS.map(({ value }, index) => [value, index])
);

const formatSupportedProtocolLabel = (protocol) => {
  const normalizedProtocol = String(protocol ?? '').trim().toLowerCase();

  if (!normalizedProtocol) {
    return '';
  }

  return PROTOCOL_LABELS[normalizedProtocol] || String(protocol).trim();
};

const compareSupportedProtocols = (left, right) => {
  const leftOrder = PROTOCOL_SORT_ORDER[left] ?? Number.MAX_SAFE_INTEGER;
  const rightOrder = PROTOCOL_SORT_ORDER[right] ?? Number.MAX_SAFE_INTEGER;

  if (leftOrder !== rightOrder) {
    return leftOrder - rightOrder;
  }

  return formatSupportedProtocolLabel(left).localeCompare(formatSupportedProtocolLabel(right));
};

const CompareContainer = styled.div`
  padding: 8px 0 clamp(24px, 3vw, 36px);
  width: min(var(--site-max-width), calc(100% - (var(--site-gutter) * 2)));
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
  margin-top: 20px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 16px;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: #64748b;
`;

const SmartCompareSection = styled.section`
  background: white;
  border: 1px solid #dbeafe;
  border-radius: 18px;
  padding: 24px;
  margin-bottom: 32px;
  box-shadow: 0 18px 40px -34px rgba(37, 99, 235, 0.55);
`;

const SmartCompareHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const SmartCompareTitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SmartCompareTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
`;

const SmartCompareText = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 14px;
  line-height: 1.7;
`;

const SmartCompareBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  border: 1px solid ${props => (
    props.$status === 'compatible' || props.$status === 'pass' ? '#bbf7d0'
      : props.$status === 'compatible-with-caveats' || props.$status === 'warning' ? '#fde68a'
        : props.$status === 'not-compatible' || props.$status === 'fail' ? '#fecaca'
          : '#cbd5e1'
  )};
  background: ${props => (
    props.$status === 'compatible' || props.$status === 'pass' ? '#f0fdf4'
      : props.$status === 'compatible-with-caveats' || props.$status === 'warning' ? '#fffbeb'
        : props.$status === 'not-compatible' || props.$status === 'fail' ? '#fef2f2'
          : '#f8fafc'
  )};
  color: ${props => (
    props.$status === 'compatible' || props.$status === 'pass' ? '#166534'
      : props.$status === 'compatible-with-caveats' || props.$status === 'warning' ? '#92400e'
        : props.$status === 'not-compatible' || props.$status === 'fail' ? '#b91c1c'
          : '#475569'
  )};
`;

const SmartCompareMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 18px;
`;

const SmartCompareMetaPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 999px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #334155;
  font-size: 13px;
  line-height: 1.4;
`;

const SmartCompareGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-top: 24px;
`;

const SmartCompareCard = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px;
  background: #ffffff;
`;

const SmartCompareCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

const SmartCompareCardTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
`;

const SmartCompareCardText = styled.p`
  margin: 0;
  color: #475569;
  font-size: 14px;
  line-height: 1.65;
`;

const SmartCompareDetailList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 12px;
`;

const SmartCompareDetailItem = styled.div`
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
`;

const SmartCompareNotes = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SmartCompareNote = styled.div`
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #475569;
  font-size: 13px;
  line-height: 1.6;
`;

const AddBoardSection = styled.div`
  background: white;
  border: 2px dashed #e2e8f0;
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  margin-bottom: 32px;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #3b82f6;
    background-color: #f8fafc;
  }
`;

const AddBoardButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #3b82f6;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #2563eb;
    transform: translateY(-1px);
  }
`;

const ComparisonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const ProtocolComparisonPanel = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  margin-bottom: 32px;
`;

const ProtocolComparisonHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
`;

const ProtocolComparisonTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  color: #1e293b;
  font-size: 20px;
  font-weight: 700;
`;

const ProtocolComparisonTableWrap = styled.div`
  overflow-x: auto;
`;

const ProtocolComparisonTable = styled.table`
  width: 100%;
  min-width: 640px;
  border-collapse: collapse;

  th,
  td {
    padding: 14px 18px;
    border-bottom: 1px solid #e2e8f0;
  }

  th {
    background: #f8fafc;
    color: #475569;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-align: left;
    text-transform: uppercase;
  }

  th:not(:first-child),
  td:not(:first-child) {
    text-align: center;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const ProtocolBoardHeader = styled.span`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const ProtocolBoardName = styled.span`
  color: #1e293b;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1.35;
  text-transform: none;
  word-break: break-word;
`;

const ProtocolNameCell = styled.td`
  color: #1e293b;
  font-size: 14px;
  font-weight: 700;
`;

const ProtocolSupportCell = styled.td`
  vertical-align: middle;
`;

const ProtocolSupportIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 999px;
  border: 1px solid ${props => props.$supported ? '#bbf7d0' : '#fecaca'};
  background: ${props => props.$supported ? '#f0fdf4' : '#fef2f2'};
  color: ${props => props.$supported ? '#166534' : '#b91c1c'};
`;

const ProtocolEmptyCell = styled.td`
  color: #64748b;
  font-size: 14px;
  line-height: 1.6;
  text-align: center;
`;

const BoardCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  position: relative;
`;

const BoardHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid #e2e8f0;
  position: relative;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #dc2626;
    transform: scale(1.1);
  }
`;

const BoardTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
  padding-right: 40px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const BoardTitleLink = styled(Link)`
  color: inherit;
  text-decoration: none;
  min-width: 0;
  overflow-wrap: anywhere;
  transition: color 0.2s ease;

  &:hover,
  &:focus {
    color: #2563eb;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  &:focus-visible {
    outline: 2px solid #93c5fd;
    outline-offset: 3px;
    border-radius: 4px;
  }
`;

const BoardSubtitle = styled.p`
  color: #64748b;
  font-size: 14px;
  margin-bottom: 16px;
`;

const BoardSpecs = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 16px;
`;

const SpecItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SpecLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SpecValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
`;

const BoardContent = styled.div`
  padding: 24px;
`;

const SectionTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DescriptionBody = styled.div`
  ${richContentBodyStyles}
  color: #64748b;
  font-size: 14px;
  line-height: 1.5;
  text-align: left;
`;

const SVGContainer = styled.div`
  height: 500px;
  min-height: 400px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  background: #f8fafc;
  
  @media (max-width: 768px) {
    height: 400px;
    min-height: 300px;
  }
`;

const LinkContainer = styled.div`
  margin-top: 16px;
`;

const DocumentationLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #3b82f6;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f1f5f9;
    border-color: #3b82f6;
  }
`;

const LoadingCard = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 40px;
  text-align: center;
  color: #64748b;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
`;

const EmptyTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
`;

const EmptyDescription = styled.p`
  font-size: 16px;
  margin-bottom: 24px;
`;

const BoardSelectionContainer = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
`;

const SelectionHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
`;

const SelectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 16px 0;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  background: white;
  color: #1e293b;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &::placeholder {
    color: #94a3b8;
  }
`;

const BoardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  padding: 24px;
  max-height: 400px;
  overflow-y: auto;
`;

const BoardSelectionItem = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  border: 2px solid ${props => props.selected ? '#3b82f6' : '#e2e8f0'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.selected ? '#f0f9ff' : 'white'};
  
  &:hover {
    border-color: #3b82f6;
    background: #f8fafc;
  }
`;

const BoardSelectionInfo = styled.div`
  flex: 1;
  margin-left: 12px;
`;

const BoardSelectionName = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
`;

const BoardSelectionDetails = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
`;

const SelectionCheckbox = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid ${props => props.checked ? '#3b82f6' : '#e2e8f0'};
  border-radius: 4px;
  background: ${props => props.checked ? '#3b82f6' : 'white'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  svg {
    color: white;
    width: 12px;
    height: 12px;
  }
`;

const BoardIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  min-width: 24px;
  line-height: 1;
  color: #f59e0b;
  flex-shrink: 0;

  svg {
    width: 22px;
    height: 22px;
    stroke-width: 2.25;
  }
`;

const SelectionFooter = styled.div`
  padding: 24px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8fafc;
`;

const SelectionCount = styled.span`
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
`;

const StartCompareButton = styled.button`
  background: #2c9fa4;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #1c6568;
  }
  
  &:disabled {
    background: #94a3b8;
    cursor: not-allowed;
  }
`;

const PinInfoPanel = styled.div`
  margin-top: 16px;
  background: white;
  border-radius: 12px;
  border: 1px solid #dbeafe;
  box-shadow: 0 10px 30px -24px rgba(37, 99, 235, 0.65);
  overflow: hidden;
  min-height: 180px;
`;

const PinInfoHeader = styled.div`
  padding: 20px 24px 16px;
  border-bottom: 1px solid #dbeafe;
  background: linear-gradient(180deg, #f8fbff 0%, #f1f5f9 100%);
  border-radius: 12px 12px 0 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const PinInfoTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
`;

const PinInfoSubtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
`;

const PinInfoContent = styled.div`
  padding: 20px 24px;
`;

const PinInfoBody = styled(motion.div)`
  display: flex;
  flex-direction: column;
`;

const PinInfoRow = styled.div`
  display: grid;
  grid-template-columns: minmax(96px, 120px) 1fr;
  gap: 12px;
  align-items: start;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
  
  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
    gap: 6px;
  }
`;

const PinInfoLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
`;

const PinInfoValue = styled.div`
  font-size: 14px;
  color: #1e293b;
  line-height: 1.6;
  word-break: break-word;

  p {
    margin: 0;
  }

  p + p {
    margin-top: 8px;
  }
`;

const PinInfoCloseButton = styled.button`
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  flex-shrink: 0;
  
  &:hover {
    background: #f1f5f9;
    color: #1e293b;
  }
`;

const PinInfoEmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 110px;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  background: #f8fafc;
  color: #64748b;
  font-size: 14px;
  line-height: 1.6;
  text-align: center;
  padding: 20px;
`;

const Copyright = styled.div`
  text-align: center;
  margin-top: 40px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
  color: #6b7280;
  font-size: 0.9rem;
`;

const CompareBoards = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [boards, setBoards] = useState([]);
  const [allBoards, setAllBoards] = useState([]);
  const [boardPins, setBoardPins] = useState({}); // Store pins for each board
  const [selectedPins, setSelectedPins] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBoardSelection, setShowBoardSelection] = useState(false);
  const [selectedBoards, setSelectedBoards] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  // Get board identifiers from URL parameters (memoized to prevent infinite loops).
  const boardIds = useMemo(() => (
    parseCompareBoardIdentifiersFromSearch(location.search)
  ), [location.search]);

  useEffect(() => {
    if (boardIds.length === 0) {
      setLoading(false);
      setShowBoardSelection(true);
      return;
    }

    setShowBoardSelection(false);

    const fetchBoards = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await boardsAPI.compare(boardIds.join(','));
        
        if (response.data && Array.isArray(response.data)) {
          // The public detail page loads the full SVG payload separately. Do the
          // same here so compare has the source artwork, stored rotation, and
          // callouts needed to build the corrected public rendering.
          const boardResults = await Promise.all(response.data.map(async (board) => {
            const [pinsResult, svgResult] = await Promise.allSettled([
              boardsAPI.getPins(board.id),
              boardsAPI.getSvg(board.id)
            ]);

            if (pinsResult.status === 'rejected') {
              console.error(`Error fetching pins for board ${board.id}:`, pinsResult.reason);
            }
            if (svgResult.status === 'rejected') {
              console.error(`Error fetching SVG for board ${board.id}:`, svgResult.reason);
            }

            const svgPayload = svgResult.status === 'fulfilled' ? svgResult.value.data : null;

            return {
              board: withBoardCompatibilityDefaults({
                ...board,
                ...(svgPayload || {})
              }),
              boardId: board.id,
              pins: pinsResult.status === 'fulfilled' && Array.isArray(pinsResult.value.data)
                ? pinsResult.value.data.map((pin) => withPinCompatibilityDefaults(pin))
                : []
            };
          }));

          const nextBoards = boardResults.map(({ board }) => board);
          setBoards(nextBoards);

          const pinsMap = {};
          boardResults.forEach(({ boardId, pins }) => {
            pinsMap[boardId] = pins;
          });
          setBoardPins(pinsMap);

          const canonicalComparePath = buildComparePath(nextBoards);
          const currentComparePath = `${location.pathname}${location.search}`;

          if (nextBoards.length > 0 && canonicalComparePath !== currentComparePath) {
            navigate(canonicalComparePath, { replace: true });
          }
        } else {
          setError('Invalid response from server');
        }
      } catch (err) {
        console.error('Error fetching boards for comparison:', err);
        setError(`Failed to load boards for comparison: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchBoards();
  }, [boardIds, location.pathname, location.search, navigate]);

  useEffect(() => {
    const activeBoardIds = new Set(boards.map((board) => String(board.id)));

    setSelectedPins((current) => {
      const filteredEntries = Object.entries(current).filter(([boardId]) => activeBoardIds.has(String(boardId)));

      if (filteredEntries.length === Object.keys(current).length) {
        return current;
      }

      return Object.fromEntries(filteredEntries);
    });
  }, [boards]);

  // Fetch all boards for selection
  useEffect(() => {
    if (showBoardSelection) {
      const fetchAllBoards = async () => {
        try {
          const response = await boardsAPI.getAll({ include_svg: 0 });
          setAllBoards(response.data);
        } catch (err) {
          console.error('Error fetching all boards:', err);
        }
      };
      fetchAllBoards();
    }
  }, [showBoardSelection]);

  const addMoreBoards = () => {
    // Pre-populate selectedBoards with currently compared board IDs
    const currentBoardIds = new Set(boards.map(board => board.id.toString()));
    setSelectedBoards(currentBoardIds);
    setShowBoardSelection(true);
  };

  const toggleBoardSelection = (boardId) => {
    const boardIdStr = boardId.toString();
    const newSelection = new Set(selectedBoards);
    
    if (newSelection.has(boardIdStr)) {
      newSelection.delete(boardIdStr);
    } else if (newSelection.size < 2) { // Limit to 2 boards
      newSelection.add(boardIdStr);
    }
    setSelectedBoards(newSelection);
  };

  const handleStartComparison = () => {
    if (selectedBoards.size >= 2) {
      const selectedBoardRecords = resolveCompareBoardSelection(Array.from(selectedBoards), [...boards, ...allBoards]);
      setShowBoardSelection(false);
      navigate(buildComparePath(selectedBoardRecords));
    }
  };

  const removeBoard = (boardIdToRemove) => {
    const remainingBoards = boards.filter(board => board.id !== boardIdToRemove);
    if (remainingBoards.length === 0) {
      // If no boards left, go back to home
      navigate('/');
    } else {
      // Update URL with remaining boards.
      navigate(buildComparePath(remainingBoards));
    }
  };

  const filteredBoards = allBoards.filter(board => {
    // Filter by search term
    const matchesSearch = searchTerm === '' || 
      board.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      board.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      board.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // When adding more boards, exclude boards that are already being compared
    const isAlreadyCompared = boards.some(comparedBoard => comparedBoard.id === board.id);
    
    return matchesSearch && !isAlreadyCompared;
  });

  const smartCompare = useMemo(() => {
    if (boards.length !== 2) {
      return null;
    }

    return buildSmartCompare(
      boards[0],
      boardPins[boards[0]?.id] || [],
      boards[1],
      boardPins[boards[1]?.id] || []
    );
  }, [boards, boardPins]);

  const protocolComparison = useMemo(() => {
    if (boards.length !== 2) {
      return {
        rows: [],
        supportSets: []
      };
    }

    const supportSets = boards.map((board) => new Set(normalizeSupportedProtocols(board?.supported_protocols)));
    const allProtocols = Array.from(new Set(supportSets.flatMap((protocolSet) => Array.from(protocolSet))));

    return {
      rows: allProtocols
        .sort(compareSupportedProtocols)
        .map((protocol) => ({
          protocol,
          label: formatSupportedProtocolLabel(protocol)
        })),
      supportSets
    };
  }, [boards]);

  const publicPreviewSvgs = useMemo(() => Object.fromEntries(boards.map((board) => {
    const pins = boardPins[board.id] || [];
    const sourceSvg = board.svg_source_content || board.svg_content || '';

    if (!sourceSvg) {
      return [board.id, ''];
    }

    const boardRotation = normalizeRotation(board.svg_rotation_deg ?? 0);
    const publicRotation = normalizeRotation(board.public_svg_rotation_deg ?? 0);
    const finalRotation = normalizeRotation(boardRotation + publicRotation);
    const storedViewBox = (
      normalizeSvgViewBox(board.svg_viewbox ?? board.svgViewBox)
      || parseSvgViewBoxFromMarkup(board.svg_content || sourceSvg)
    );
    const storedCallouts = Array.isArray(board.pin_callouts) ? board.pin_callouts : [];
    const callouts = finalRotation === boardRotation
      ? storedCallouts
      : rotateCallouts(storedCallouts, sourceSvg, boardRotation, finalRotation);
    const viewBox = !storedViewBox || finalRotation === boardRotation
      ? storedViewBox
      : rotateSvgViewBox(storedViewBox, sourceSvg, boardRotation, finalRotation);

    return [board.id, buildPinoutPreviewSvg(sourceSvg, pins, callouts, finalRotation, {
      viewBoundsMode: 'board',
      lockedViewBox: viewBox || null,
      normalizeCalloutSize: false,
    })];
  })), [boards, boardPins]);

  // Handle pin clicks
  const handlePinClick = (arg, boardId) => {
    let payload = (arg && typeof arg === 'object' && 'pin' in arg) ? arg : null;

    if (!payload && arg?.target) {
      const el = arg.target.closest ? arg.target.closest('.pin-rect, .pin-hole, .pin-callout-bubble, .pin-callout-anchor') : null;
      if (el) {
        payload = {
          pin: el.getAttribute('data-pin') || '',
          svgId: el.getAttribute('data-svgid') || el.id || '',
          pinNumber: el.getAttribute('data-pin-number') || ''
        };
      }
    }

    if (!payload) return;

    const pins = boardPins[boardId] || [];
    const { pin: pinName, svgId, pinNumber } = payload;
    const pin = pins.find((currentPin) =>
      (pinName && currentPin.pin_name === pinName) ||
      (svgId && currentPin.svg_id === svgId) ||
      (pinNumber && String(currentPin.pin_number) === String(pinNumber))
    );

    if (!pin) return;

    setSelectedPins((current) => ({
      ...current,
      [boardId]: pin
    }));
  };

  const clearSelectedPin = (boardId) => {
    setSelectedPins((current) => {
      if (!current[boardId]) {
        return current;
      }

      const next = { ...current };
      delete next[boardId];
      return next;
    });
  };

  const getBoardIcon = (name) => {
    if (name.toLowerCase().includes('arduino')) return '🟠';
    if (name.toLowerCase().includes('raspberry')) return '🍓';
    if (name.toLowerCase().includes('esp')) return '📡';
    if (name.toLowerCase().includes('feather')) return '🪶';
    return '🔌';
  };

  const getCompareBoardIcon = (board) => {
    if (isMicrocontrollerCategory(board?.category)) {
      return <Cpu size={20} aria-hidden="true" />;
    }

    return getBoardCategoryIcon(board?.category) || getBoardIcon(board?.name || '');
  };

  if (loading) {
    return (
      <CompareContainer>
        <Header>
          <Title>Compare Boards</Title>
          <Subtitle>Loading boards for comparison...</Subtitle>
        </Header>

        <ComparisonGrid>
          {boardIds.map((id, index) => (
            <LoadingCard key={id}>
              Loading board {index + 1}...
            </LoadingCard>
          ))}
        </ComparisonGrid>
      </CompareContainer>
    );
  }

  if (error) {
    return (
      <CompareContainer>
        <Header>
          <Title>Compare Boards</Title>
          <Subtitle>Compare specifications and features</Subtitle>
        </Header>

        <ErrorMessage>{error}</ErrorMessage>
      </CompareContainer>
    );
  }

  if (boards.length === 0) {
    return (
      <CompareContainer>       
        <Header>
          <Title>Compare Boards</Title>
          <Subtitle>Compare specifications and features</Subtitle>
        </Header>

        {showBoardSelection ? (
          <BoardSelectionContainer>
            <SelectionHeader>
              <SelectionTitle>Select Boards to Compare</SelectionTitle>
              <SearchInput
                type="text"
                placeholder="Search boards by name, manufacturer, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SelectionHeader>

            <BoardsGrid>
              {filteredBoards.map((board) => (
                <BoardSelectionItem
                  key={board.id}
                  selected={selectedBoards.has(board.id.toString())}
                  onClick={() => toggleBoardSelection(board.id)}
                >
                  <SelectionCheckbox checked={selectedBoards.has(board.id.toString())}>
                    {selectedBoards.has(board.id.toString()) && <Check size={12} />}
                  </SelectionCheckbox>
                  <BoardIcon>{getCompareBoardIcon(board)}</BoardIcon>
                  <BoardSelectionInfo>
                    <BoardSelectionName>{board.name}</BoardSelectionName>
                    <BoardSelectionDetails>
                      {board.manufacturer} • {board.pin_count} pins • {board.package_type}
                    </BoardSelectionDetails>
                  </BoardSelectionInfo>
                </BoardSelectionItem>
              ))}
            </BoardsGrid>

            <SelectionFooter>
              <SelectionCount>
                {selectedBoards.size === 0 
                  ? 'Select at least 2 boards to compare'
                  : `${selectedBoards.size} board${selectedBoards.size !== 1 ? 's' : ''} selected`
                }
              </SelectionCount>
              <StartCompareButton
                onClick={handleStartComparison}
                disabled={selectedBoards.size < 2}
              >
                <GitCompare size={16} />
                Start Comparison
              </StartCompareButton>
            </SelectionFooter>
          </BoardSelectionContainer>
        ) : (
          <EmptyState>
            <EmptyTitle>No boards selected for comparison</EmptyTitle>
            <EmptyDescription>
              Select boards from the home page to compare their specifications, pinouts, and features.
            </EmptyDescription>
            <AddBoardButton onClick={addMoreBoards}>
              <Plus size={20} />
              Browse Boards
            </AddBoardButton>
          </EmptyState>
        )}
      </CompareContainer>
    );
  }

  return (
    <CompareContainer>     
      <Header>
        <Title>Compare Boards</Title>
        <Subtitle>
          {boards.length === 1 
            ? 'Board details and specifications' 
            : `Compare ${boards.length} boards side by side`
          }
        </Subtitle>
      </Header>

      {showBoardSelection ? (
        <BoardSelectionContainer>
          <SelectionHeader>
            <SelectionTitle>Add More Boards to Comparison</SelectionTitle>
            <SearchInput
              type="text"
              placeholder="Search boards by name, manufacturer, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SelectionHeader>

          <BoardsGrid>
            {filteredBoards.map((board) => (
              <BoardSelectionItem
                key={board.id}
                selected={selectedBoards.has(board.id.toString())}
                onClick={() => toggleBoardSelection(board.id)}
              >
                <SelectionCheckbox checked={selectedBoards.has(board.id.toString())}>
                  {selectedBoards.has(board.id.toString()) && <Check size={12} />}
                </SelectionCheckbox>
                <BoardIcon>{getCompareBoardIcon(board)}</BoardIcon>
                <BoardSelectionInfo>
                  <BoardSelectionName>{board.name}</BoardSelectionName>
                  <BoardSelectionDetails>
                    {board.manufacturer} • {board.pin_count} pins • {board.package_type}
                  </BoardSelectionDetails>
                </BoardSelectionInfo>
              </BoardSelectionItem>
            ))}
          </BoardsGrid>

          <SelectionFooter>
            <SelectionCount>
              {selectedBoards.size === 0 
                ? 'Select at least 2 boards to compare'
                : `${selectedBoards.size} board${selectedBoards.size !== 1 ? 's' : ''} selected`
              }
            </SelectionCount>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowBoardSelection(false)}
                style={{
                  background: 'none',
                  border: '1px solid #e2e8f0',
                  color: '#64748b',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <StartCompareButton
                onClick={handleStartComparison}
                disabled={selectedBoards.size < 2}
              >
                <GitCompare size={16} />
                Start Comparison
              </StartCompareButton>
            </div>
          </SelectionFooter>
        </BoardSelectionContainer>
      ) : (
        boards.length < 2 && (
          <AddBoardSection>
            <h3 style={{ marginBottom: '16px', color: '#1e293b' }}>
              Want to compare more boards?
            </h3>
            <p style={{ marginBottom: '24px', color: '#64748b' }}>
              You can compare up to 2 boards at once. Add more boards to get a comprehensive comparison.
            </p>
            <AddBoardButton onClick={addMoreBoards}>
              <Plus size={20} />
              Add More Boards
            </AddBoardButton>
          </AddBoardSection>
        )
      )}

      {!showBoardSelection && boards.length === 2 && smartCompare?.available && (
        <SmartCompareSection>
          <SmartCompareHeader>
            <SmartCompareTitleWrap>
              <SmartCompareTitle>
                <GitCompare size={22} />
                Smart Compare
              </SmartCompareTitle>
              <SmartCompareText>
                Compatibility summary for the currently selected MCU and sensor pair.
              </SmartCompareText>
            </SmartCompareTitleWrap>
            <SmartCompareBadge $status={smartCompare.overallStatus}>
              {smartCompare.overallStatus === 'compatible' ? 'Compatible'
                : smartCompare.overallStatus === 'compatible-with-caveats' ? 'Compatible With Caveats'
                  : smartCompare.overallStatus === 'not-compatible' ? 'Needs Changes'
                    : 'Needs More Data'}
            </SmartCompareBadge>
          </SmartCompareHeader>

          <SmartCompareText style={{ marginTop: '16px' }}>
            {smartCompare.summary}
          </SmartCompareText>

          <SmartCompareMeta>
            <SmartCompareMetaPill>
              <strong>MCU:</strong> {smartCompare.boards.mcu.name}
            </SmartCompareMetaPill>
            <SmartCompareMetaPill>
              <strong>Sensor:</strong> {smartCompare.boards.sensor.name}
            </SmartCompareMetaPill>
            {smartCompare.recommendedConnection && (
              <SmartCompareMetaPill>
                <strong>Recommended:</strong> {smartCompare.recommendedConnection}
              </SmartCompareMetaPill>
            )}
          </SmartCompareMeta>

          <SmartCompareGrid>
            {smartCompare.checks.map((check) => (
              <SmartCompareCard key={check.key}>
                <SmartCompareCardHeader>
                  <SmartCompareCardTitle>{check.label}</SmartCompareCardTitle>
                  <SmartCompareBadge $status={check.status}>
                    {check.status === 'pass'
                      ? 'Pass'
                      : check.status === 'warning'
                        ? 'Caution'
                        : check.status === 'fail'
                          ? 'Fail'
                          : 'Unknown'}
                  </SmartCompareBadge>
                </SmartCompareCardHeader>
                <SmartCompareCardText>{check.message}</SmartCompareCardText>
                {Array.isArray(check.details) && check.details.length > 0 && (
                  <SmartCompareDetailList>
                    {check.details.map((detail, detailIndex) => (
                      <SmartCompareDetailItem key={`${check.key}-${detailIndex}`}>
                        {detail}
                      </SmartCompareDetailItem>
                    ))}
                  </SmartCompareDetailList>
                )}
              </SmartCompareCard>
            ))}
          </SmartCompareGrid>

          {smartCompare.notes.length > 0 && (
            <SmartCompareNotes>
              {smartCompare.notes.map((note, noteIndex) => (
                <SmartCompareNote key={`smart-note-${noteIndex}`}>
                  {note}
                </SmartCompareNote>
              ))}
            </SmartCompareNotes>
          )}
        </SmartCompareSection>
      )}

      <ComparisonGrid>
        <AnimatePresence>
          {boards.map((board, index) => (
            <BoardCard
              key={board.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ delay: index * 0.1 }}
            >
              {(() => {
                const selectedPin = selectedPins[board.id];
                const pinDisplayName = selectedPin?.pin_name || (selectedPin ? `Pin ${selectedPin.pin_number}` : 'Pin Details');
                const pinSubtitle = selectedPin
                  ? `Pin ${selectedPin.pin_number}${selectedPin.group_name ? ` - ${selectedPin.group_name}` : ''}`
                  : 'Click a pin on the diagram to inspect its details here.';

                return (
                  <>
              <BoardHeader>
                <RemoveButton onClick={() => removeBoard(board.id)}>
                  <X size={16} />
                </RemoveButton>
                
                <BoardTitle>
                  <BoardIcon>{getCompareBoardIcon(board)}</BoardIcon>
                  <BoardTitleLink to={`/board/${board.slug || board.id}`}>
                    {board.name}
                  </BoardTitleLink>
                </BoardTitle>
                <BoardSubtitle>{board.manufacturer}</BoardSubtitle>
                
                <BoardSpecs>
                  <SpecItem>
                    <SpecLabel>Package</SpecLabel>
                    <SpecValue>{board.package_type || 'N/A'}</SpecValue>
                  </SpecItem>
                  <SpecItem>
                    <SpecLabel>Pins</SpecLabel>
                    <SpecValue>{board.pin_count || 'N/A'}</SpecValue>
                  </SpecItem>
                  <SpecItem>
                    <SpecLabel>Voltage</SpecLabel>
                    <SpecValue>{formatBoardVoltage(board) || 'N/A'}</SpecValue>
                  </SpecItem>
                  <SpecItem>
                    <SpecLabel>CPU</SpecLabel>
                    <SpecValue>{board.cpu || 'N/A'}</SpecValue>
                  </SpecItem>
                  <SpecItem>
                    <SpecLabel>Clock</SpecLabel>
                    <SpecValue>{board.clock_speed || 'N/A'}</SpecValue>
                  </SpecItem>
                  <SpecItem>
                    <SpecLabel>Flash</SpecLabel>
                    <SpecValue>{board.flash_memory || 'N/A'}</SpecValue>
                  </SpecItem>
                  <SpecItem>
                    <SpecLabel>RAM</SpecLabel>
                    <SpecValue>{board.ram || 'N/A'}</SpecValue>
                  </SpecItem>
                </BoardSpecs>
              </BoardHeader>

              <BoardContent>
                <SectionTitle>
                  <Info size={16} />
                  Description
                </SectionTitle>
                <DescriptionBody
                  dangerouslySetInnerHTML={{
                    __html: board.description || 'No description available'
                  }}
                />

                {board.link && (
                  <LinkContainer>
                    <DocumentationLink href={board.link} target="_blank" rel="noopener noreferrer">
                      <LinkIcon size={16} />
                      Documentation
                    </DocumentationLink>
                  </LinkContainer>
                )}

                {publicPreviewSvgs[board.id] && (
                  <>
                    <SectionTitle style={{ marginTop: '24px' }}>
                      <Zap size={16} />
                      Pinout Diagram
                    </SectionTitle>
                    <SVGContainer>
                      <SVGViewer
                        svgContent={publicPreviewSvgs[board.id]}
                        pins={boardPins[board.id] || []}
                        displayScale={board.svg_display_scale}
                        onPinClick={(e) => handlePinClick(e, board.id)}
                        initialZoom={1}
                        minZoom={0.2}
                        maxZoom={3}
                        enableZoom={true}
                        enablePan={true}
                        enableWheelZoom={false}
                        showWheelZoomToggle={true}
                        style={{ height: '100%', width: '100%' }}
                      />
                    </SVGContainer>
                    <PinInfoPanel>
                      <PinInfoHeader>
                        <div>
                          <PinInfoTitle>{pinDisplayName}</PinInfoTitle>
                          <PinInfoSubtitle>{pinSubtitle}</PinInfoSubtitle>
                        </div>
                        {selectedPin && (
                          <PinInfoCloseButton onClick={() => clearSelectedPin(board.id)} aria-label={`Clear selected pin for ${board.name}`}>
                            <X size={18} />
                          </PinInfoCloseButton>
                        )}
                      </PinInfoHeader>

                      <PinInfoContent>
                        <AnimatePresence mode="wait" initial={false}>
                          {selectedPin ? (
                            <PinInfoBody
                              key={selectedPin.id || `${selectedPin.pin_number}-${selectedPin.svg_id || ''}`}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              transition={{ duration: 0.18 }}
                            >
                              <PinInfoRow>
                                <PinInfoLabel>Pin Number</PinInfoLabel>
                                <PinInfoValue>{selectedPin.pin_number}</PinInfoValue>
                              </PinInfoRow>

                              {selectedPin.pin_name && (
                                <PinInfoRow>
                                  <PinInfoLabel>Pin Name</PinInfoLabel>
                                  <PinInfoValue>{selectedPin.pin_name}</PinInfoValue>
                                </PinInfoRow>
                              )}

                              {selectedPin.functions && (
                                <PinInfoRow>
                                  <PinInfoLabel>Functions</PinInfoLabel>
                                  <PinInfoValue>{selectedPin.functions}</PinInfoValue>
                                </PinInfoRow>
                              )}

                              {selectedPin.alternate_functions && (
                                <PinInfoRow>
                                  <PinInfoLabel>Alternate</PinInfoLabel>
                                  <PinInfoValue>{selectedPin.alternate_functions}</PinInfoValue>
                                </PinInfoRow>
                              )}

                              {formatPinVoltage(selectedPin) && (
                                <PinInfoRow>
                                  <PinInfoLabel>Voltage</PinInfoLabel>
                                  <PinInfoValue>{formatPinVoltage(selectedPin)}</PinInfoValue>
                                </PinInfoRow>
                              )}

                              {formatPinCurrent(selectedPin) && (
                                <PinInfoRow>
                                  <PinInfoLabel>Current</PinInfoLabel>
                                  <PinInfoValue>{formatPinCurrent(selectedPin)}</PinInfoValue>
                                </PinInfoRow>
                              )}

                              {selectedPin.group_name && (
                                <PinInfoRow>
                                  <PinInfoLabel>Group</PinInfoLabel>
                                  <PinInfoValue>{selectedPin.group_name}</PinInfoValue>
                                </PinInfoRow>
                              )}

                              {selectedPin.description && (
                                <PinInfoRow>
                                  <PinInfoLabel>Description</PinInfoLabel>
                                  <PinInfoValue dangerouslySetInnerHTML={{ __html: selectedPin.description }} />
                                </PinInfoRow>
                              )}
                            </PinInfoBody>
                          ) : (
                            <PinInfoEmptyState>
                              Click any highlighted pin to keep its pin number, functions, voltage, and notes right below this board.
                            </PinInfoEmptyState>
                          )}
                        </AnimatePresence>
                      </PinInfoContent>
                    </PinInfoPanel>
                  </>
                )}
              </BoardContent>
                  </>
                );
              })()}
            </BoardCard>
          ))}
        </AnimatePresence>
      </ComparisonGrid>

      {boards.length === 2 && (
        <ProtocolComparisonPanel>
          <ProtocolComparisonHeader>
            <ProtocolComparisonTitle>
              <GitCompare size={20} />
              Supported Protocols
            </ProtocolComparisonTitle>
          </ProtocolComparisonHeader>
          <ProtocolComparisonTableWrap>
            <ProtocolComparisonTable>
              <thead>
                <tr>
                  <th>All Supported Protocols</th>
                  <th>
                    <ProtocolBoardHeader>
                      First Board
                      <ProtocolBoardName>{boards[0]?.name || 'Board 1'}</ProtocolBoardName>
                    </ProtocolBoardHeader>
                  </th>
                  <th>
                    <ProtocolBoardHeader>
                      Second Board
                      <ProtocolBoardName>{boards[1]?.name || 'Board 2'}</ProtocolBoardName>
                    </ProtocolBoardHeader>
                  </th>
                </tr>
              </thead>
              <tbody>
                {protocolComparison.rows.length > 0 ? (
                  protocolComparison.rows.map((row) => (
                    <tr key={row.protocol}>
                      <ProtocolNameCell>{row.label}</ProtocolNameCell>
                      {[0, 1].map((boardIndex) => {
                        const isSupported = protocolComparison.supportSets[boardIndex]?.has(row.protocol);
                        const boardName = boards[boardIndex]?.name || `Board ${boardIndex + 1}`;
                        const supportLabel = `${boardName} ${isSupported ? 'supports' : 'does not support'} ${row.label}`;

                        return (
                          <ProtocolSupportCell key={`${row.protocol}-${boardIndex}`}>
                            <ProtocolSupportIndicator
                              $supported={isSupported}
                              aria-label={supportLabel}
                              role="img"
                              title={supportLabel}
                            >
                              {isSupported ? <Check size={16} aria-hidden="true" /> : <X size={16} aria-hidden="true" />}
                            </ProtocolSupportIndicator>
                          </ProtocolSupportCell>
                        );
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <ProtocolEmptyCell colSpan={3}>
                      No supported protocol metadata has been added for these boards yet.
                    </ProtocolEmptyCell>
                  </tr>
                )}
              </tbody>
            </ProtocolComparisonTable>
          </ProtocolComparisonTableWrap>
        </ProtocolComparisonPanel>
      )}

      <Copyright>
        <strong>&copy; 2026 micropinouts.com</strong><br />
        All rights reserved.<br />
        Educational use and community sharing encouraged under applicable open-source licenses.
      </Copyright>
    </CompareContainer>
  );
};

export default CompareBoards;
